// ============================================================
// EFFECT ITEMS - the effects a sheet's named bundles hold (plan
// modes-rework: the Always on items, the Solo modes and the squad
// abilities all hold these), read off storage and said in words.
//
// AN EFFECT HERE IS AN EFFECT ANYWHERE. A bundle holds the same records a
// talent or trait declares (`TalentEffect`) - a skill bonus, a damage
// bonus, a called-shot reduction, an extra reaction, an initiative
// modifier, an exposure (what OTHERS get against the bearer) - so nothing
// downstream needs to know a bundle exists: `collectCharacterEffects`
// reads `character.permanentEffects` beside the catalog's, and what is up
// reaches it through a derived sheet (`engine/roster/stancedSheet`).
// Only the kinds the resolver actually applies are offered (`EFFECT_KINDS`).
//
// Which bundles are up, and how they switch, is `soloModes`.
//
// Pure. Zero React dependency.
// ============================================================
import { SKILL_TEST_DEFS } from '../characteristicTests';
import { describeExposure, parseExposure } from '../exposures';
import { isSelfOnly, readWhen, whenSentence } from './effectWhen';
import { CHARACTERISTICS } from './characteristicList';
import { TALENT_CATALOG } from './talentCatalog';
import { TRAIT_CATALOG } from './traitCatalog';
import type { When } from './effectWhen';
import type { TalentEffect } from './talentEffects';

/** The effect kinds a stance may hold: the ones the resolver applies from a plain record. */
export const EFFECT_KINDS = [
  'skill_bonus',
  'damage_bonus',
  'called_shot_reduction',
  'defence_grant',
  'initiative_modifier',
  'exposure',
  'attack_reroll',
  // The sheet tier (plan armoury, stage 3): what a stance may change on the sheet itself.
  'characteristic_bonus',
  'unnatural_characteristic',
  'grant_talent',
  'grant_trait',
  'wound_bonus',
  'flat_ap_bonus',
  'resistance',
] as const;
export type EffectKind = (typeof EFFECT_KINDS)[number];

/**
 * The SHEET TIER: kinds applied to the character itself (`effectiveSheet`), worked out before anybody is targeted
 * or anything armed. One may wait only on a condition about oneself (`isSelfOnly`): Toughness +10 while wounded.
 */
const SHEET_KINDS: readonly EffectKind[] = [
  'characteristic_bonus',
  'unnatural_characteristic',
  'grant_talent',
  'grant_trait',
  'wound_bonus',
  'flat_ap_bonus',
  'resistance',
];
export const isSheetKind = (type: string): boolean => (SHEET_KINDS as readonly string[]).includes(type);

/**
 * An effect a stance (or the always-on list) holds. `when` makes it apply
 * only in some situations (`effectWhen`); `source` is the stance it came
 * from, set on the derived sheet so a line in the ledger can name it.
 */
export type StanceEffect = Extract<TalentEffect, { type: EffectKind }> & { when?: When; source?: string };

/**
 * May this effect carry a condition? Only what is applied TO AN ATTACK,
 * where the situation is known: a bonus to the attack skill, to damage, or a re-roll
 * (`engine/character/situational`). Anything else is resolved with nobody
 * in particular in front of the character.
 */
export function takesWhen(effect: Pick<StanceEffect, 'type'> & { skill?: string }): boolean {
  if (effect.type === 'damage_bonus' || effect.type === 'attack_reroll') return true;
  return effect.type === 'skill_bonus' && (ATTACK_SKILLS as readonly string[]).includes(effect.skill ?? '');
}

const MAX_NAME = 40;
const SCOPES = ['melee', 'ranged', 'all'] as const;
const DEFENCES = ['dodge', 'parry'] as const;
const REROLLS = ['attack', 'damage'] as const;
/** What a skill bonus may be on: the two attack skills, or a catalog skill. */
const ATTACK_SKILLS = ['ws', 'bs'] as const;

const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const text = (value: unknown, max = MAX_NAME): string => (typeof value === 'string' ? value.trim().slice(0, max) : '');
const oneOf = <T extends string>(list: readonly T[], value: unknown): T | null =>
  list.includes(value as T) ? (value as T) : null;
const isSkill = (id: unknown): id is string =>
  typeof id === 'string' &&
  ((ATTACK_SKILLS as readonly string[]).includes(id) || SKILL_TEST_DEFS.some((s) => s.id === id));

type Raw = Record<string, unknown>;

/** How each kind is read off storage: the record this build can apply, or null. A lookup, so a new kind is a row. */
const READERS: { [K in EffectKind]: (raw: Raw) => Extract<StanceEffect, { type: K }> | null } = {
  skill_bonus: (raw) => {
    const value = num(raw.value);
    return value !== null && isSkill(raw.skill) ? { type: 'skill_bonus', value, skill: raw.skill } : null;
  },
  damage_bonus: (raw) => {
    const value = num(raw.value);
    const scope = oneOf(SCOPES, raw.scope);
    return value !== null && scope ? { type: 'damage_bonus', value, scope } : null;
  },
  called_shot_reduction: (raw) => {
    const value = num(raw.value);
    const scope = oneOf(SCOPES, raw.scope);
    return value !== null && scope ? { type: 'called_shot_reduction', value, scope } : null;
  },
  defence_grant: (raw) => {
    const additional = num(raw.additional);
    const defence = oneOf(DEFENCES, raw.defence);
    return additional !== null && defence ? { type: 'defence_grant', defence, additional } : null;
  },
  initiative_modifier: (raw) => {
    const value = num(raw.value);
    return value !== null ? { type: 'initiative_modifier', value } : null;
  },
  exposure: (raw) =>
    typeof raw.code === 'string' && parseExposure(raw.code) ? { type: 'exposure', code: raw.code } : null,
  attack_reroll: (raw) => {
    const of = oneOf(REROLLS, raw.of);
    return of ? { type: 'attack_reroll', of } : null;
  },
  characteristic_bonus: (raw) => {
    const value = num(raw.value);
    const characteristic = oneOf(CHARACTERISTIC_IDS, raw.characteristic);
    return value !== null && characteristic ? { type: 'characteristic_bonus', characteristic, value } : null;
  },
  unnatural_characteristic: (raw) => {
    const multiplier = num(raw.multiplier);
    const characteristic = oneOf(CHARACTERISTIC_IDS, raw.characteristic);
    return multiplier !== null && multiplier >= 1 && characteristic
      ? { type: 'unnatural_characteristic', characteristic, multiplier }
      : null;
  },
  grant_talent: (raw) =>
    granted(raw, 'talentId', TALENT_CATALOG, (talentId, p) => ({ type: 'grant_talent', talentId, ...p })),
  grant_trait: (raw) =>
    granted(raw, 'traitId', TRAIT_CATALOG, (traitId, p) => ({ type: 'grant_trait', traitId, ...p })),
  wound_bonus: (raw) => {
    const value = num(raw.value);
    return value !== null ? { type: 'wound_bonus', value } : null;
  },
  flat_ap_bonus: (raw) => {
    const value = num(raw.value);
    return value !== null ? { type: 'flat_ap_bonus', value } : null;
  },
  resistance: (raw) => {
    const value = num(raw.value);
    const against = text(raw.against);
    return value !== null && against ? { type: 'resistance', value, against } : null;
  },
};

const CHARACTERISTIC_IDS = CHARACTERISTICS.map((c) => c.id);

/** A granted talent or trait off storage: one the catalogue knows, with its parameter when it has one. */
function granted<E>(
  raw: Raw,
  key: 'talentId' | 'traitId',
  catalog: Record<string, unknown>,
  make: (id: string, extra: { parameter?: string }) => E,
): E | null {
  const id = typeof raw[key] === 'string' ? (raw[key] as string) : '';
  if (!Object.hasOwn(catalog, id)) return null;
  const parameter = text(raw.parameter);
  return make(id, parameter ? { parameter } : {});
}

/** One stored effect, validated; null for a kind a stance may not hold or a record this build cannot apply. */
export function readEffect(raw: unknown): StanceEffect | null {
  if (!raw || typeof raw !== 'object') return null;
  const kind = oneOf(EFFECT_KINDS, (raw as Raw).type);
  const effect = kind ? READERS[kind](raw as Raw) : null;
  return effect ? conditioned(effect, raw as Raw) : null;
}

/**
 * The effect with its source and its condition, if it has them. A condition
 * this build cannot read, or one on a kind that cannot carry it, LEAVES THE
 * EFFECT OUT: applying it unconditionally would be a different effect.
 */
function conditioned(effect: StanceEffect, raw: Raw): StanceEffect | null {
  const source = text(raw.source);
  const out = source ? { ...effect, source } : effect;
  const none = raw.when === undefined || (Array.isArray(raw.when) && raw.when.length === 0);
  if (none) return out;
  const when = readWhen(raw.when);
  // An attack effect takes any condition; a sheet effect only one about oneself (the two tiers).
  const fits = takesWhen(effect) || (!!when && isSheetKind(effect.type) && isSelfOnly(when));
  return when && fits ? { ...out, when } : null;
}

/** A stored list of effects, with whatever cannot be read left out. */
export function readEffects(raw: unknown): StanceEffect[] {
  return Array.isArray(raw) ? raw.map(readEffect).filter((e): e is StanceEffect => e !== null) : [];
}

const signed = (n: number) => (n >= 0 ? `+${n}` : String(n));
const SCOPE_WORDS = { melee: ' in melee', ranged: ' at range', all: '' } as const;
const SKILL_NAMES: Record<string, string> = { ws: 'Weapon Skill', bs: 'Ballistic Skill' };
const skillName = (id: string | undefined) =>
  SKILL_NAMES[id ?? ''] ?? SKILL_TEST_DEFS.find((s) => s.id === id)?.label ?? id;

/** An exposure in words. A custom one reads "Exposed (To hit -10)" on a chip; in a sentence, just what it does. */
function exposureWords(code: string): string {
  const said = describeExposure(code) ?? 'an exposure';
  const custom = /^Exposed \((.*)\)$/.exec(said);
  return custom ? custom[1] : said;
}

/** How each kind reads in a sentence: "Weapon Skill +10", "Called Shot penalty: waived". */
const SENTENCES: { [K in EffectKind]: (effect: Extract<StanceEffect, { type: K }>) => string } = {
  skill_bonus: (e) => `${skillName(e.skill)} ${signed(e.value)}`,
  damage_bonus: (e) => `Damage ${signed(e.value)}${SCOPE_WORDS[e.scope]}`,
  called_shot_reduction: (e) =>
    e.value >= 20
      ? `Called Shot penalty${SCOPE_WORDS[e.scope]}: waived`
      : `Called Shot penalty${SCOPE_WORDS[e.scope]}: ${e.value} less`,
  defence_grant: (e) =>
    `${e.additional === 1 ? 'One extra' : `${e.additional} extra`} ${e.defence === 'dodge' ? 'Dodge' : 'Parry'} each round`,
  initiative_modifier: (e) => `Initiative ${signed(e.value)}`,
  exposure: (e) => `Those who attack me: ${exposureWords(e.code)}`,
  attack_reroll: (e) => (e.of === 'damage' ? 'Re-roll damage' : 'Re-roll a missed attack'),
  characteristic_bonus: (e) => `${characteristicName(e.characteristic)} ${signed(e.value)}`,
  unnatural_characteristic: (e) => `Unnatural ${characteristicName(e.characteristic)} (x${e.multiplier})`,
  grant_talent: (e) => `Gains the talent ${grantName(TALENT_CATALOG, e.talentId, e.parameter)}`,
  grant_trait: (e) => `Gains the trait ${grantName(TRAIT_CATALOG, e.traitId, e.parameter)}`,
  wound_bonus: (e) => `Wounds ${signed(e.value)}`,
  flat_ap_bonus: (e) => `Armour ${signed(e.value)} everywhere`,
  resistance: (e) => `Resistance (${e.against}) ${signed(e.value)}`,
};

const characteristicName = (id: string) => CHARACTERISTICS.find((c) => c.id === id)?.name ?? id;
function grantName(catalog: Record<string, { name: string }>, id: string, parameter?: string): string {
  const name = catalog[id]?.name ?? id;
  return parameter ? `${name} (${parameter})` : name;
}

/** An effect as the tab, the strip and the ledger say it. */
export function effectSentence(effect: StanceEffect): string {
  const said = (SENTENCES[effect.type] as (e: StanceEffect) => string)(effect);
  return effect.when ? `${said}, when ${whenSentence(effect.when)}` : said;
}
